import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Users, 
  MessageSquare,
  Image,
  FileText,
  AlertTriangle,
  CheckCircle,
  X,
  Eye,
  Edit,
  Trash2,
  Search,
  Filter,
  RefreshCw,
  TrendingUp,
  Calendar,
  MapPin,
  Heart,
  MessageCircle,
  BookOpen,
  Camera,
  Star,
  Shield,
  UserCheck,
  UserX,
  Globe,
  Flag,
  BarChart3,
  Activity,
  Settings,
  Download,
  Upload,
  Plus,
  MoreHorizontal,
  ShoppingBag
} from 'lucide-react';
import { 
  Card, 
  Table, 
  Tag, 
  Button, 
  Input, 
  Select, 
  Modal, 
  Form, 
  message,
  Row,
  Col,
  Statistic,
  Badge,
  Avatar,
  Tooltip,
  Dropdown,
  Space,
  Typography,
  Progress,
  Tabs,
  Drawer,
  Empty
} from 'antd';
import { useNavigate } from 'react-router-dom';
import { db, appId } from '../firebase/firebaseConfig.jsx';
import { 
  collection, 
  query, 
  orderBy, 
  limit, 
  getDocs, 
  doc, 
  updateDoc, 
  deleteDoc,
  where,
  startAfter,
  getCountFromServer,
  addDoc,
  serverTimestamp,
  writeBatch
} from 'firebase/firestore';
import logger from '../utils/logger.jsx';
import dayjs from 'dayjs';

const { Text } = Typography;
const { TabPane } = Tabs;
const { Option } = Select;
const { Search: AntSearch } = Input;


const CommunityManagementPage = () => {
  // State management
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedItems, setSelectedItems] = useState([]);

  // Data states
  const [groups, setGroups] = useState([]);
  const [studyGroups, setStudyGroups] = useState([]);
  const [learningResources, setLearningResources] = useState([]);
  const [partners, setPartners] = useState([]);
  const [storyPosts, setStoryPosts] = useState([]);
  const [photoPosts, setPhotoPosts] = useState([]);
  const [memoryPosts, setMemoryPosts] = useState([]);
  const [messages, setMessages] = useState([]);
  const [marketplaceItems, setMarketplaceItems] = useState([]);
  const [marketplaceFeeLedger, setMarketplaceFeeLedger] = useState([]);
  const [communityPosts, setCommunityPosts] = useState([]); // 🔥 Community Board (소통의 장)
  
  
  // Statistics
  const [stats, setStats] = useState({
    totalGroups: 0,
    totalStudyGroups: 0,
    totalLearningResources: 0,
    totalPartners: 0,
    totalStoryPosts: 0,
    totalPhotoPosts: 0,
    totalMemoryPosts: 0,
    totalMessages: 0,
    totalMarketplaceItems: 0,
    totalCommunityPosts: 0, // 🔥 Community Board
    activeUsers: 0,
    totalInteractions: 0
  });

  // Modal states
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [viewItem, setViewItem] = useState(null);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [createForm] = Form.useForm();
  const [editForm] = Form.useForm();

  // Tab configuration
  const tabs = [
    { 
      key: 'overview', 
      label: 'Overview', 
      icon: BarChart3, 
      color: 'blue',
      description: 'Community statistics and overview'
    },
    { 
      key: 'groups', 
      label: 'Groups', 
      icon: Users, 
      color: 'blue',
      description: 'Manage community groups'
    },
    { 
      key: 'studyGroups', 
      label: 'Study Groups', 
      icon: BookOpen, 
      color: 'green',
      description: 'Manage study groups'
    },
    { 
      key: 'learningResources', 
      label: 'Learning Resources', 
      icon: FileText, 
      color: 'purple',
      description: 'Manage learning materials'
    },
    { 
      key: 'partners', 
      label: 'Partners', 
      icon: UserCheck, 
      color: 'orange',
      description: 'Manage partner profiles'
    },
    { 
      key: 'storyPosts', 
      label: 'Stories', 
      icon: MessageSquare, 
      color: 'indigo',
      description: 'Manage story posts'
    },
    { 
      key: 'photoPosts', 
      label: 'Photos', 
      icon: Camera, 
      color: 'pink',
      description: 'Manage photo posts'
    },
    { 
      key: 'memoryPosts', 
      label: 'Memories', 
      icon: Heart, 
      color: 'rose',
      description: 'Manage memory posts'
    },
    { 
      key: 'communityPosts', 
      label: 'Community Board', 
      icon: MessageSquare, 
      color: 'blue',
      description: 'Manage community board posts (소통의 장)'
    },
    { 
      key: 'messages', 
      label: 'Messages', 
      icon: MessageCircle, 
      color: 'teal',
      description: 'Manage user messages'
    }
    // Marketplace → /marketplace-management 전용 메뉴로 이동
  ];

  const statusOptions = [
    { value: 'all', label: 'All' },
    { value: 'active', label: 'Active' },
    { value: 'available', label: 'Available' },
    { value: 'reserved', label: 'Reserved' },
    { value: 'sold', label: 'Sold' },
    { value: 'completed', label: 'Completed' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'pending', label: 'Pending' },
    { value: 'rejected', label: 'Rejected' },
    { value: 'featured', label: 'Featured' }
  ];

  const marketplaceFeeStats = useMemo(() => {
    const totalFeeKRW = marketplaceFeeLedger.reduce((s, e) => s + (Number(e.feeKRW) || 0), 0);
    return {
      count: marketplaceFeeLedger.length,
      totalFeeKRW,
    };
  }, [marketplaceFeeLedger]);

  // Load data based on active tab
  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    setLoading(true);
    try {
      switch (activeTab) {
        case 'overview':
          await Promise.all([loadOverviewData(), loadCommunityPosts()]);
          break;
        case 'groups':
          await loadGroups();
          break;
        case 'studyGroups':
          await loadStudyGroups();
          break;
        case 'learningResources':
          await loadLearningResources();
          break;
        case 'partners':
          await loadPartners();
          break;
        case 'storyPosts':
          await loadStoryPosts();
          break;
        case 'photoPosts':
          await loadPhotoPosts();
          break;
        case 'memoryPosts':
          await loadMemoryPosts();
          break;
        case 'communityPosts':
          await loadCommunityPosts();
          break;
        case 'messages':
          await loadMessages();
          break;
        case 'marketplace':
          await loadMarketplaceItems();
          break;
        default:
          break;
      }
    } catch (error) {
      console.error('데이터 로딩 실패:', error);
      logger.error('CommunityManagementPage loadData error', { error: error.message });
      message.error('데이터 로딩에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // Load overview statistics - Updated to use actual frontend collections
  const loadOverviewData = async () => {
    try {
      const collections = [
        { name: 'groups', path: 'groups' },
        { name: 'studyGroups', path: 'koreanStudyGroups' }, 
        { name: 'learningResources', path: 'koreanLearningResources' },
        { name: 'partners', path: 'studyPartnerProfiles' },
        { name: 'myanmarNews', path: `artifacts/${appId}/myanmarNews` },
        { name: 'myanmarMemories', path: `artifacts/${appId}/myanmarMemories` },
        { name: 'myanmarPhotosVideos', path: `artifacts/${appId}/myanmarPhotosVideos` },
        { name: 'communityPosts', path: `artifacts/${appId}/community_posts` },
        { name: 'marketplaceItems', path: `artifacts/${appId}/marketplace` },
        { name: 'privateMessages', path: 'privateMessages' },
        { name: 'chatMessages', path: `artifacts/${appId}/messages` }
      ];

      const statsData = {};
      
      for (const { name, path } of collections) {
        try {
          const q = query(collection(db, path));
          const snapshot = await getCountFromServer(q);
          const count = snapshot.data().count;
          
          // 통계 키 생성 (첫 글자 대문자)
          const statKey = `total${name.charAt(0).toUpperCase() + name.slice(1)}`;
          statsData[statKey] = count;
          
          console.log(`✅ ${name} count: ${count} from ${path}`);
        } catch (error) {
          console.warn(`⚠️ Failed to load ${name} from ${path}:`, error);
          const statKey = `total${name.charAt(0).toUpperCase() + name.slice(1)}`;
          statsData[statKey] = 0;
        }
      }
      
      // Myanmar Story 통계 (뉴스 + 추억 + 사진)
      statsData.totalStoryPosts = (statsData.totalMyanmarNews || 0);
      statsData.totalMemoryPosts = (statsData.totalMyanmarMemories || 0);
      statsData.totalPhotoPosts = (statsData.totalMyanmarPhotosVideos || 0);
      
      // Messages 통계 (개인 메시지 + 채팅 메시지)
      statsData.totalMessages = (statsData.totalPrivateMessages || 0) + (statsData.totalChatMessages || 0);

      // Load active users (users who have created content in the last 30 days)
      try {
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        
        const activeUsersQuery = query(
          collection(db, `artifacts/${appId}/users`),
          where('lastActiveAt', '>=', thirtyDaysAgo)
        );
        const activeUsersSnapshot = await getCountFromServer(activeUsersQuery);
        statsData.activeUsers = activeUsersSnapshot.data().count;
      } catch (error) {
        console.warn('Failed to load active users:', error);
        statsData.activeUsers = 0;
      }

      setStats(statsData);
    } catch (error) {
      console.error('Overview data loading failed:', error);
    }
  };

  // Load specific data types - Updated to use actual frontend collections
  const loadGroups = async () => {
    try {
      const q = query(
        collection(db, 'groups'),
        orderBy('createdAt', 'desc'),
        limit(100)
      );
      const snapshot = await getDocs(q);
      const groupsData = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          title: data.title || data.name || 'Untitled Group',
          description: data.description || data.content || 'No description',
          authorName: data.authorName || data.userName || data.leaderName || 'Unknown',
          authorEmail: data.authorEmail || data.userEmail || data.leaderEmail || '',
          status: data.status || (data.isActive === false ? 'inactive' : 'active'),
          createdAt: data.createdAt?.toDate?.() || new Date(),
          updatedAt: data.updatedAt?.toDate?.() || new Date(),
          memberCount: data.memberCount || data.members?.length || 0,
          category: data.category || data.type || 'General',
          region: data.region || data.location || 'Unknown'
        };
      });
      setGroups(groupsData);
      logger.info('Groups loaded from frontend collection', { count: groupsData.length });
    } catch (error) {
      console.error('Groups loading failed:', error);
      logger.error('Failed to load groups from frontend', error);
      setGroups([]);
    }
  };

  const loadStudyGroups = async () => {
    try {
      const q = query(
        collection(db, 'koreanStudyGroups'),
        orderBy('createdAt', 'desc'),
        limit(100)
      );
      const snapshot = await getDocs(q);
      const studyGroupsData = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          title: data.title || data.name || 'Untitled Study Group',
          description: data.description || data.content || 'No description',
          authorName: data.authorName || data.userName || data.leaderName || 'Unknown',
          authorEmail: data.authorEmail || data.userEmail || data.leaderEmail || '',
          status: data.status || (data.isActive === false ? 'inactive' : 'active'),
          createdAt: data.createdAt?.toDate?.() || new Date(),
          updatedAt: data.updatedAt?.toDate?.() || new Date(),
          memberCount: data.memberCount || data.members?.length || 0,
          subject: data.subject || data.topic || 'General',
          level: data.level || data.difficulty || 'Beginner'
        };
      });
      setStudyGroups(studyGroupsData);
      logger.info('Study groups loaded from frontend collection', { count: studyGroupsData.length });
    } catch (error) {
      console.error('Study groups loading failed:', error);
      logger.error('Failed to load study groups from frontend', error);
      setStudyGroups([]);
    }
  };

  const loadLearningResources = async () => {
    try {
      const q = query(
        collection(db, 'koreanLearningResources'),
        orderBy('createdAt', 'desc'),
        limit(50)
      );
      const snapshot = await getDocs(q);
      const learningResourcesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate?.() || new Date()
      }));
      setLearningResources(learningResourcesData);
      logger.info('Learning resources loaded from frontend collection', { count: learningResourcesData.length });
    } catch (error) {
      console.error('Learning resources loading failed:', error);
      logger.error('Failed to load learning resources from frontend', error);
      setLearningResources([]);
    }
  };

  const loadPartners = async () => {
    try {
      const q = query(
        collection(db, 'studyPartnerProfiles'),
        orderBy('createdAt', 'desc'),
        limit(50)
      );
      const snapshot = await getDocs(q);
      const partnersData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate?.() || new Date()
      }));
      setPartners(partnersData);
      logger.info('Study partners loaded from frontend collection', { count: partnersData.length });
    } catch (error) {
      console.error('Partners loading failed:', error);
      logger.error('Failed to load study partners from frontend', error);
      setPartners([]);
    }
  };


  const loadStoryPosts = async () => {
    try {
      // 🔥 Frontend 실제 경로: artifacts/${appId}/myanmarNews
      const q = query(
        collection(db, `artifacts/${appId}/myanmarNews`),
        orderBy('createdAt', 'desc'),
        limit(50)
      );
      const snapshot = await getDocs(q);
      const storyPostsData = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          ...data,
          id: doc.id,
          title: data.title || data.headline || 'Untitled News',
          description: data.description || data.content || data.summary || 'No description',
          authorName: data.authorName || data.author || 'Unknown',
          authorEmail: data.authorEmail || '',
          status: data.status || (data.isPublished === false ? 'inactive' : 'active'),
          createdAt: data.createdAt?.toDate?.() || new Date(),
          updatedAt: data.updatedAt?.toDate?.() || new Date(),
          category: data.category || 'General',
          language: data.language || 'my',
          viewCount: data.viewCount || 0,
          likeCount: data.likes || data.likeCount || 0,
          comments: data.comments || 0,
          type: 'news'
        };
      });
      setStoryPosts(storyPostsData);
      logger.info('Myanmar News (Story Posts) loaded from frontend collection', { count: storyPostsData.length });
    } catch (error) {
      console.error('Story posts loading failed:', error);
      logger.error('Failed to load story posts from frontend', error);
      setStoryPosts([]);
    }
  };

  const loadPhotoPosts = async () => {
    try {
      // 🔥 Frontend 실제 경로: artifacts/${appId}/myanmarPhotosVideos
      const q = query(
        collection(db, `artifacts/${appId}/myanmarPhotosVideos`),
        orderBy('createdAt', 'desc'),
        limit(50)
      );
      const snapshot = await getDocs(q);
      const photoPostsData = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          ...data,
          id: doc.id,
          title: data.title || data.caption || 'Untitled Photo',
          description: data.description || data.caption || 'No description',
          authorName: data.authorName || data.author || 'Unknown',
          authorEmail: data.authorEmail || '',
          status: data.status || (data.isPublished === false ? 'inactive' : 'active'),
          createdAt: data.createdAt?.toDate?.() || new Date(),
          updatedAt: data.updatedAt?.toDate?.() || new Date(),
          imageUrl: data.imageUrl || data.photoUrl || data.videoUrl || '',
          likeCount: data.likes || data.likeCount || 0,
          comments: data.comments || 0,
          type: 'photo'
        };
      });
      setPhotoPosts(photoPostsData);
      logger.info('Myanmar Photos/Videos loaded from frontend collection', { count: photoPostsData.length });
    } catch (error) {
      console.error('Photo posts loading failed:', error);
      logger.error('Failed to load photo posts from frontend', error);
      setPhotoPosts([]);
    }
  };

  const loadMemoryPosts = async () => {
    try {
      // 🔥 Frontend 실제 경로: artifacts/${appId}/myanmarMemories
      const q = query(
        collection(db, `artifacts/${appId}/myanmarMemories`),
        orderBy('createdAt', 'desc'),
        limit(50)
      );
      const snapshot = await getDocs(q);
      const memoryPostsData = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          ...data,
          id: doc.id,
          title: data.title || data.memoryTitle || 'Untitled Memory',
          description: data.description || data.content || data.memoryContent || 'No description',
          authorName: data.authorName || data.author || 'Unknown',
          authorEmail: data.authorEmail || '',
          status: data.status || (data.isPublished === false ? 'inactive' : 'active'),
          createdAt: data.createdAt?.toDate?.() || new Date(),
          updatedAt: data.updatedAt?.toDate?.() || new Date(),
          imageUrl: data.imageUrl || '',
          likeCount: data.likes || data.likeCount || 0,
          comments: data.comments || 0,
          type: 'memory'
        };
      });
      setMemoryPosts(memoryPostsData);
      logger.info('Myanmar Memories loaded from frontend collection', { count: memoryPostsData.length });
    } catch (error) {
      console.error('Memory posts loading failed:', error);
      logger.error('Failed to load memory posts from frontend', error);
      setMemoryPosts([]);
    }
  };

  const loadMessages = async () => {
    try {
      // 🔥 Frontend 실제 경로: privateMessages (루트 컬렉션) 및 artifacts/${appId}/messages (채팅 메시지)
      const allMessages = [];
      
      // 1. Private Messages (개인 메시지)
      try {
        const privateMessagesQuery = query(
          collection(db, 'privateMessages'),
          orderBy('timestamp', 'desc'),
          limit(50)
        );
        const privateMessagesSnapshot = await getDocs(privateMessagesQuery);
        const privateMessagesData = privateMessagesSnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            title: data.subject || 'Private Message',
            description: data.content || data.message || 'No content',
            authorName: data.senderName || 'Unknown',
            authorEmail: data.senderId || data.senderEmail || '',
            status: data.read ? 'read' : 'unread',
            createdAt: data.timestamp?.toDate?.() || new Date(),
            updatedAt: data.timestamp?.toDate?.() || new Date(),
            recipientName: data.recipientName || 'Unknown',
            recipientEmail: data.recipientId || data.recipientEmail || '',
            messageType: data.type || 'private_message',
            priority: data.urgent ? 'high' : 'normal',
            source: 'privateMessages'
          };
        });
        allMessages.push(...privateMessagesData);
      } catch (error) {
        console.warn('Failed to load private messages:', error);
      }
      
      // 2. Chat Messages (채팅 메시지)
      try {
        const chatMessagesQuery = query(
          collection(db, `artifacts/${appId}/messages`),
          orderBy('createdAt', 'desc'),
          limit(50)
        );
        const chatMessagesSnapshot = await getDocs(chatMessagesQuery);
        const chatMessagesData = chatMessagesSnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            title: data.title || data.subject || 'Chat Message',
            description: data.content || data.message || data.body || 'No content',
            authorName: data.senderName || data.fromName || data.authorName || 'Unknown',
            authorEmail: data.senderEmail || data.fromEmail || data.authorEmail || '',
            status: data.status || (data.isRead ? 'read' : 'unread'),
            createdAt: data.createdAt?.toDate?.() || new Date(),
            updatedAt: data.updatedAt?.toDate?.() || new Date(),
            recipientName: data.recipientName || data.toName || 'Unknown',
            recipientEmail: data.recipientEmail || data.toEmail || '',
            messageType: data.type || data.messageType || 'chat',
            priority: data.priority || (data.urgent ? 'high' : 'normal'),
            source: 'chatMessages'
          };
        });
        allMessages.push(...chatMessagesData);
      } catch (error) {
        console.warn('Failed to load chat messages:', error);
      }
      
      // 날짜순으로 정렬
      allMessages.sort((a, b) => {
        const dateA = a.createdAt instanceof Date ? a.createdAt : new Date(a.createdAt);
        const dateB = b.createdAt instanceof Date ? b.createdAt : new Date(b.createdAt);
        return dateB - dateA;
      });
      
      setMessages(allMessages.slice(0, 100));
      logger.info('Messages loaded from frontend collections', { 
        privateMessages: allMessages.filter(m => m.source === 'privateMessages').length,
        chatMessages: allMessages.filter(m => m.source === 'chatMessages').length,
        total: allMessages.length
      });
    } catch (error) {
      console.error('Messages loading failed:', error);
      logger.error('Failed to load messages from frontend', error);
      setMessages([]);
    }
  };

  const loadCommunityPosts = async () => {
    try {
      // 🔥 Frontend 실제 경로: artifacts/${appId}/community_posts
      const q = query(
        collection(db, `artifacts/${appId}/community_posts`),
        orderBy('createdAt', 'desc'),
        limit(100)
      );
      const snapshot = await getDocs(q);
      const communityPostsData = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          title: data.title || 'Untitled Post',
          description: data.content || data.description || 'No content',
          authorName: data.authorName || data.userName || 'Unknown',
          authorEmail: data.authorEmail || data.userEmail || '',
          status: data.status || (data.isActive === false ? 'inactive' : 'active'),
          createdAt: data.createdAt?.toDate?.() || new Date(),
          updatedAt: data.updatedAt?.toDate?.() || new Date(),
          likeCount: data.likeCount || data.likes || 0,
          comments: data.commentCount || data.comments || 0,
          category: data.category || 'General',
          authorId: data.authorId || data.userId || ''
        };
      });
      setCommunityPosts(communityPostsData);
      logger.info('Community Board posts loaded from frontend collection', { count: communityPostsData.length });
    } catch (error) {
      console.error('Community posts loading failed:', error);
      logger.error('Failed to load community posts from frontend', error);
      setCommunityPosts([]);
    }
  };

  const loadMarketplaceItems = async () => {
    try {
      // 🔥 Frontend 실제 경로: artifacts/${appId}/marketplace
      const q = query(
        collection(db, `artifacts/${appId}/marketplace`),
        orderBy('createdAt', 'desc'),
        limit(100)
      );
      const snapshot = await getDocs(q);
      const marketplaceData = snapshot.docs.map(doc => {
        const data = doc.data();
        const rawStatus = data.status || (data.isActive === false ? 'inactive' : 'available');
        return {
          id: doc.id,
          title: data.title || 'Untitled Item',
          description: data.description || 'No description',
          authorName: data.userName || data.authorName || 'Unknown',
          authorEmail: data.userEmail || data.authorEmail || '',
          status: rawStatus,
          createdAt: data.createdAt?.toDate?.() || new Date(),
          updatedAt: data.updatedAt?.toDate?.() || new Date(),
          price: data.price || 0,
          feeKRW: data.feeKRW ?? null,
          netAmountKRW: data.netAmountKRW ?? null,
          completedAt: data.completedAt?.toDate?.() || null,
          category: data.category || 'General',
          // App uses selling/buying; legacy admin used sell/buy — normalize for display
          type: (() => {
            const raw = data.type || 'selling';
            if (raw === 'buy' || raw === 'buying') return 'buying';
            if (raw === 'sell' || raw === 'selling') return 'selling';
            return raw;
          })(),
          contactMethod: data.contactMethod || 'message',
          imageUrl: data.imageUrl || data.photos?.[0] || '',
          location: data.location || 'Unknown',
          condition: data.condition || 'good',
          photos: data.photos || [] // 🔥 사진 배열 추가
        };
      });
      setMarketplaceItems(marketplaceData);

      try {
        const feeQ = query(
          collection(db, `artifacts/${appId}/marketplaceFeeLedger`),
          orderBy('createdAt', 'desc'),
          limit(200)
        );
        const feeSnap = await getDocs(feeQ);
        const feeRows = feeSnap.docs.map((d) => {
          const data = d.data() || {};
          return {
            id: d.id,
            itemId: data.itemId || '',
            title: data.title || '',
            sellerId: data.sellerId || '',
            priceKRW: Number(data.priceKRW) || 0,
            feeKRW: Number(data.feeKRW) || 0,
            netAmountKRW: Number(data.netAmountKRW) || 0,
            createdAt: data.createdAt?.toDate?.() || null,
          };
        });
        setMarketplaceFeeLedger(feeRows);
      } catch (feeErr) {
        // 컬렉션/인덱스가 아직 없으면 아이템 feeKRW로 폴백
        logger.warn('marketplaceFeeLedger load failed', { error: feeErr.message });
        const fromItems = marketplaceData
          .filter((i) => i.feeKRW != null && Number(i.feeKRW) > 0)
          .map((i) => ({
            id: `item-${i.id}`,
            itemId: i.id,
            title: i.title,
            sellerId: '',
            priceKRW: Number(i.price) || 0,
            feeKRW: Number(i.feeKRW) || 0,
            netAmountKRW: Number(i.netAmountKRW) || 0,
            createdAt: i.completedAt || i.updatedAt,
          }));
        setMarketplaceFeeLedger(fromItems);
      }

      logger.info('Marketplace items loaded from frontend collection', { count: marketplaceData.length });
    } catch (error) {
      console.error('Marketplace items loading failed:', error);
      logger.error('Failed to load marketplace items from frontend', error);
      setMarketplaceItems([]);
      setMarketplaceFeeLedger([]);
    }
  };

  // Handle actions
  const handleSearch = (value) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleStatusChange = (value) => {
    setFilterStatus(value);
    setCurrentPage(1);
  };

  const handleRefresh = () => {
    loadData();
    message.success('데이터가 새로고침되었습니다.');
  };

  const handleCreate = () => {
    if (activeTab === 'learningResources') {
      // Learning Resources creation form
      createForm.setFieldsValue({
        title: '',
        description: '',
        content: '',
        category: 'grammar',
        level: 'beginner',
        language: 'ko',
        tags: [],
        isPublic: true,
        authorName: 'Admin'
      });
    } else if (activeTab === 'marketplace') {
      // Marketplace creation form
      createForm.setFieldsValue({
        title: '',
        description: '',
        price: '',
        category: '',
        type: 'selling',
        contactMethod: 'message',
        imageUrl: '',
        location: '',
        condition: 'good',
        status: 'active'
      });
    } else if (activeTab === 'communityPosts') {
      // Community Board creation form
      createForm.setFieldsValue({
        title: '',
        content: '',
        category: 'General',
        status: 'active'
      });
    } else {
      // Generic creation form
      createForm.setFieldsValue({
        title: '',
        description: '',
        category: '',
        status: 'active'
      });
    }
    setIsCreateModalOpen(true);
  };

  const handleEdit = (item) => {
    setSelectedItem(item);
    if (activeTab === 'marketplace') {
      editForm.setFieldsValue({
        title: item.title,
        description: item.description,
        price: item.price,
        category: item.category,
        type: item.type,
        contactMethod: item.contactMethod,
        imageUrl: item.imageUrl,
        location: item.location,
        condition: item.condition,
        status: item.status
      });
    } else if (activeTab === 'communityPosts') {
      editForm.setFieldsValue({
        title: item.title,
        content: item.description || item.content,
        category: item.category,
        status: item.status
      });
    } else {
      editForm.setFieldsValue(item);
    }
    setIsEditModalOpen(true);
  };

  const TAB_COLLECTIONS = {
    groups: 'groups',
    studyGroups: 'koreanStudyGroups',
    learningResources: 'koreanLearningResources',
    partners: 'studyPartnerProfiles',
    storyPosts: `artifacts/${appId}/myanmarNews`,
    photoPosts: `artifacts/${appId}/myanmarPhotosVideos`,
    memoryPosts: `artifacts/${appId}/myanmarMemories`,
    communityPosts: `artifacts/${appId}/community_posts`,
    marketplace: `artifacts/${appId}/marketplace`,
  };

  const getCollectionPath = (item) => {
    if (activeTab === 'messages') {
      return item.source === 'privateMessages' ? 'privateMessages' : `artifacts/${appId}/messages`;
    }
    return TAB_COLLECTIONS[activeTab];
  };

  const getRowKey = (record) => `${activeTab}-${record.source || ''}-${record.id}`;

  const handleDelete = (item) => {
    const path = getCollectionPath(item);
    if (!path) return;
    Modal.confirm({
      title: '이 항목을 삭제할까요?',
      content: (
        <div className="text-[13px]">
          <div className="font-semibold text-slate-800">{getItemTitle(item)}</div>
          <div className="mt-1 text-slate-500">삭제한 항목은 되돌릴 수 없습니다.</div>
        </div>
      ),
      okText: '삭제',
      okType: 'danger',
      cancelText: '취소',
      onOk: async () => {
        try {
          await deleteDoc(doc(db, path, item.id));
          message.success('삭제되었습니다.');
          setViewItem((prev) => (prev?.id === item.id ? null : prev));
          setSelectedItems((prev) => prev.filter((k) => k !== getRowKey(item)));
          loadData();
          loadOverviewData();
        } catch (error) {
          console.error('Delete failed:', error);
          message.error('삭제에 실패했습니다: ' + error.message);
        }
      },
    });
  };

  const handleBulkDelete = () => {
    const targets = filteredData.filter((r) => selectedItems.includes(getRowKey(r)));
    if (targets.length === 0) return;
    Modal.confirm({
      title: `선택한 ${targets.length}개 항목을 삭제할까요?`,
      content: '삭제한 항목은 되돌릴 수 없습니다.',
      okText: `${targets.length}개 삭제`,
      okType: 'danger',
      cancelText: '취소',
      onOk: async () => {
        setBulkDeleting(true);
        try {
          for (let i = 0; i < targets.length; i += 450) {
            const batch = writeBatch(db);
            targets.slice(i, i + 450).forEach((item) => {
              const path = getCollectionPath(item);
              if (path) batch.delete(doc(db, path, item.id));
            });
            await batch.commit();
          }
          message.success(`${targets.length}개 항목이 삭제되었습니다.`);
          setSelectedItems([]);
          loadData();
          loadOverviewData();
        } catch (error) {
          console.error('Bulk delete failed:', error);
          message.error('일괄 삭제에 실패했습니다: ' + error.message);
        } finally {
          setBulkDeleting(false);
        }
      },
    });
  };

  const handleTabChange = (key) => {
    setActiveTab(key);
    setSearchTerm('');
    setFilterStatus('all');
    setCurrentPage(1);
    setSelectedItems([]);
  };

  const handleView = (item) => {
    setViewItem(item);
  };

  // Learning Resources specific actions
  const handleCreateLearningResource = async (values) => {
    try {
      const resourceData = {
        ...values,
        id: `admin_resource_${Date.now()}`,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        authorId: 'admin',
        authorName: values.authorName || 'Kairos Admin',
        authorEmail: 'admin@kairos.com',
        viewCount: 0,
        likeCount: 0,
        downloadCount: 0,
        isApproved: true,
        status: 'active'
      };

      await addDoc(collection(db, 'koreanLearningResources'), resourceData);
      message.success('학습 자료가 성공적으로 생성되었습니다.');
      setIsCreateModalOpen(false);
      createForm.resetFields();
      loadLearningResources();
      loadOverviewData();
    } catch (error) {
      console.error('Learning resource creation failed:', error);
      message.error('학습 자료 생성에 실패했습니다.');
    }
  };

  const handleUpdateLearningResource = async (values) => {
    try {
      const resourceRef = doc(db, 'koreanLearningResources', selectedItem.id);
      await updateDoc(resourceRef, {
        ...values,
        updatedAt: new Date().toISOString()
      });
      message.success('학습 자료가 성공적으로 업데이트되었습니다.');
      setIsEditModalOpen(false);
      editForm.resetFields();
      loadLearningResources();
    } catch (error) {
      console.error('Learning resource update failed:', error);
      message.error('학습 자료 업데이트에 실패했습니다.');
    }
  };

  const handleDeleteLearningResource = async () => {
    try {
      await deleteDoc(doc(db, 'koreanLearningResources', selectedItem.id));
      message.success('학습 자료가 삭제되었습니다.');
      setIsDeleteModalOpen(false);
      loadLearningResources();
    } catch (error) {
      console.error('Learning resource deletion failed:', error);
      message.error('학습 자료 삭제에 실패했습니다.');
    }
  };

  // Marketplace specific actions
  const handleCreateMarketplaceItem = async (values) => {
    try {
      const marketplaceData = {
        ...values,
        id: `admin_marketplace_${Date.now()}`,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        userId: 'admin',
        userName: 'Kairos Admin',
        userEmail: 'admin@kairos.com',
        status: values.status === 'inactive' ? 'sold' : 'available',
        likes: 0,
        viewCount: 0,
        isActive: values.status === 'active',
        isApproved: true
      };

      await addDoc(collection(db, `artifacts/${appId}/marketplace`), marketplaceData);
      message.success('중고장터 아이템이 성공적으로 생성되었습니다.');
      setIsCreateModalOpen(false);
      createForm.resetFields();
      loadMarketplaceItems();
    } catch (error) {
      console.error('Marketplace item creation failed:', error);
      message.error('중고장터 아이템 생성에 실패했습니다.');
    }
  };

  const handleUpdateMarketplaceItem = async (values) => {
    try {
      const marketplaceRef = doc(db, `artifacts/${appId}/marketplace`, selectedItem.id);
      await updateDoc(marketplaceRef, {
        ...values,
        updatedAt: new Date().toISOString(),
        isActive: values.status === 'active'
      });
      message.success('중고장터 아이템이 성공적으로 업데이트되었습니다.');
      setIsEditModalOpen(false);
      editForm.resetFields();
      loadMarketplaceItems();
    } catch (error) {
      console.error('Marketplace item update failed:', error);
      message.error('중고장터 아이템 업데이트에 실패했습니다.');
    }
  };

  const handleDeleteMarketplaceItem = async () => {
    try {
      await deleteDoc(doc(db, `artifacts/${appId}/marketplace`, selectedItem.id));
      message.success('중고장터 아이템이 삭제되었습니다.');
      setIsDeleteModalOpen(false);
      loadMarketplaceItems();
    } catch (error) {
      console.error('Marketplace item deletion failed:', error);
      message.error('중고장터 아이템 삭제에 실패했습니다.');
    }
  };

  // 🔥 Community Board specific actions
  const handleCreateCommunityPost = async (values) => {
    try {
      const postData = {
        ...values,
        content: values.content || values.description || '',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        authorId: 'admin',
        authorName: 'Kairos Admin',
        authorEmail: 'admin@kairos.com',
        likeCount: 0,
        commentCount: 0,
        isActive: values.status === 'active'
      };

      await addDoc(collection(db, `artifacts/${appId}/community_posts`), postData);
      message.success('커뮤니티 게시글이 성공적으로 생성되었습니다.');
      setIsCreateModalOpen(false);
      createForm.resetFields();
      loadCommunityPosts();
      loadOverviewData();
    } catch (error) {
      console.error('Community post creation failed:', error);
      message.error('커뮤니티 게시글 생성에 실패했습니다.');
    }
  };

  const handleUpdateCommunityPost = async (values) => {
    try {
      const postRef = doc(db, `artifacts/${appId}/community_posts`, selectedItem.id);
      await updateDoc(postRef, {
        ...values,
        content: values.content || values.description || '',
        updatedAt: serverTimestamp(),
        isActive: values.status === 'active'
      });
      message.success('커뮤니티 게시글이 성공적으로 업데이트되었습니다.');
      setIsEditModalOpen(false);
      editForm.resetFields();
      loadCommunityPosts();
    } catch (error) {
      console.error('Community post update failed:', error);
      message.error('커뮤니티 게시글 업데이트에 실패했습니다.');
    }
  };

  const handleDeleteCommunityPost = async () => {
    try {
      await deleteDoc(doc(db, `artifacts/${appId}/community_posts`, selectedItem.id));
      message.success('커뮤니티 게시글이 삭제되었습니다.');
      setIsDeleteModalOpen(false);
      loadCommunityPosts();
    } catch (error) {
      console.error('Community post deletion failed:', error);
      message.error('커뮤니티 게시글 삭제에 실패했습니다.');
    }
  };

  // Get current data based on active tab
  const getCurrentData = () => {
    switch (activeTab) {
      case 'groups': return groups;
      case 'studyGroups': return studyGroups;
      case 'learningResources': return learningResources;
      case 'partners': return partners;
      case 'storyPosts': return storyPosts;
      case 'photoPosts': return photoPosts;
      case 'memoryPosts': return memoryPosts;
      case 'communityPosts': return communityPosts; // 🔥 Community Board
      case 'messages': return messages;
      case 'marketplace': return marketplaceItems;
      default: return [];
    }
  };

  const filteredData = useMemo(() => {
    const data = getCurrentData();
    const term = searchTerm.trim().toLowerCase();

    return data.filter((item) => {
      if (filterStatus !== 'all' && item.status !== filterStatus) {
        return false;
      }
      if (!term) return true;

      const haystack = [
        item.title,
        item.name,
        item.description,
        item.summary,
        item.content,
        item.authorName,
        item.source,
        item.category,
        item.language,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return haystack.includes(term);
    });
  }, [
    activeTab,
    searchTerm,
    filterStatus,
    groups,
    studyGroups,
    learningResources,
    partners,
    storyPosts,
    photoPosts,
    memoryPosts,
    communityPosts,
    messages,
    marketplaceItems,
  ]);

  const TAB_STAT_KEY = {
    groups: 'totalGroups',
    studyGroups: 'totalStudyGroups',
    learningResources: 'totalLearningResources',
    partners: 'totalPartners',
    storyPosts: 'totalStoryPosts',
    photoPosts: 'totalPhotoPosts',
    memoryPosts: 'totalMemoryPosts',
    communityPosts: 'totalCommunityPosts',
    messages: 'totalMessages',
  };
  const TAB_ACCENT = {
    overview: 'bg-slate-100 text-slate-700',
    groups: 'bg-blue-50 text-blue-600',
    studyGroups: 'bg-emerald-50 text-emerald-600',
    learningResources: 'bg-violet-50 text-violet-600',
    partners: 'bg-orange-50 text-orange-600',
    storyPosts: 'bg-indigo-50 text-indigo-600',
    photoPosts: 'bg-pink-50 text-pink-600',
    memoryPosts: 'bg-rose-50 text-rose-600',
    communityPosts: 'bg-sky-50 text-sky-600',
    messages: 'bg-teal-50 text-teal-600',
  };
  const CREATABLE_TABS = ['learningResources', 'communityPosts'];
  const EDITABLE_TABS = ['learningResources', 'communityPosts', 'marketplace'];
  const STATUS_STYLE = {
    active: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
    available: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
    inactive: 'bg-slate-100 text-slate-600 ring-slate-200',
    pending: 'bg-amber-50 text-amber-700 ring-amber-200',
    rejected: 'bg-red-50 text-red-700 ring-red-200',
    featured: 'bg-blue-50 text-blue-700 ring-blue-200',
    reserved: 'bg-yellow-50 text-yellow-700 ring-yellow-200',
    sold: 'bg-purple-50 text-purple-700 ring-purple-200',
    completed: 'bg-cyan-50 text-cyan-700 ring-cyan-200',
    read: 'bg-slate-100 text-slate-600 ring-slate-200',
    unread: 'bg-orange-50 text-orange-700 ring-orange-200',
  };

  const navigate = useNavigate();
  const currentTab = tabs.find((tb) => tb.key === activeTab) || tabs[0];
  const canCreate = CREATABLE_TABS.includes(activeTab);
  const canEdit = EDITABLE_TABS.includes(activeTab);

  function getItemTitle(record) {
    return (
      record?.title ||
      record?.name ||
      record?.displayName ||
      record?.nickname ||
      record?.userName ||
      'Untitled'
    );
  }

  const getItemDesc = (record) =>
    record?.description && record.description !== 'No description' && record.description !== 'No content'
      ? record.description
      : record?.bio || record?.introduction || record?.content || record?.summary || '';

  const getItemImage = (record) =>
    record?.imageUrl || record?.photoUrl || record?.thumbnailUrl || record?.photos?.[0] || record?.profileImage || '';

  const toDate = (v) => (v?.toDate ? v.toDate() : v);
  const fmtDate = (v, f = 'YYYY-MM-DD HH:mm') => {
    const d = dayjs(toDate(v));
    return v && d.isValid() ? d.format(f) : '-';
  };

  const StatusPill = ({ status }) => (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold capitalize ring-1 ${
        STATUS_STYLE[status] || 'bg-slate-100 text-slate-600 ring-slate-200'
      }`}
    >
      {status || 'unknown'}
    </span>
  );

  const statusFilterOptions = useMemo(() => {
    const counts = {};
    getCurrentData().forEach((item) => {
      const st = item.status || 'unknown';
      counts[st] = (counts[st] || 0) + 1;
    });
    return Object.entries(counts).sort((x, y) => y[1] - x[1]);
  }, [activeTab, groups, studyGroups, learningResources, partners, storyPosts, photoPosts, memoryPosts, communityPosts, messages, marketplaceItems]);

  const renderMeta = (record) => {
    const chips = [];
    if (record.memberCount) chips.push(`👥 ${record.memberCount}`);
    if (record.likeCount) chips.push(`♥ ${record.likeCount}`);
    if (typeof record.comments === 'number' && record.comments > 0) chips.push(`💬 ${record.comments}`);
    if (record.viewCount) chips.push(`👁 ${record.viewCount}`);
    if (record.level) chips.push(String(record.level));
    if (record.recipientName && activeTab === 'messages') chips.push(`→ ${record.recipientName}`);
    const category = record.category || record.subject || record.messageType;
    return (
      <div className="flex flex-wrap items-center gap-1">
        {category && (
          <span className="rounded bg-slate-100 px-1.5 py-px text-[10px] font-medium text-slate-600">{String(category)}</span>
        )}
        {chips.map((c) => (
          <span key={c} className="text-[11px] text-slate-500">{c}</span>
        ))}
        {!category && chips.length === 0 && <span className="text-[11px] text-slate-300">-</span>}
      </div>
    );
  };

  const getColumns = () => [
    {
      title: 'Title',
      key: 'title',
      render: (_, record) => {
        const img = getItemImage(record);
        const title = getItemTitle(record);
        const desc = getItemDesc(record);
        return (
          <button
            type="button"
            onClick={() => handleView(record)}
            className="flex w-full !min-h-0 items-center gap-2.5 !p-0 text-left"
          >
            {img ? (
              <img src={img} alt="" className="h-9 w-9 flex-shrink-0 rounded-md object-cover ring-1 ring-slate-200" />
            ) : (
              <span className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md text-xs font-bold ${TAB_ACCENT[activeTab] || 'bg-slate-100 text-slate-600'}`}>
                {title.charAt(0).toUpperCase()}
              </span>
            )}
            <span className="min-w-0">
              <span className="block max-w-[360px] truncate text-[13px] font-semibold text-slate-900 hover:text-indigo-600">{title}</span>
              {desc && <span className="block max-w-[360px] truncate text-[11px] text-slate-500">{desc}</span>}
            </span>
          </button>
        );
      },
    },
    {
      title: 'Author',
      key: 'author',
      width: 200,
      render: (_, record) => (
        <div className="min-w-0">
          <div className="max-w-[180px] truncate text-[13px] text-slate-800">{record.authorName || record.userName || 'Unknown'}</div>
          {(record.authorEmail || record.userEmail) && (
            <div className="max-w-[180px] truncate text-[11px] text-slate-400">{record.authorEmail || record.userEmail}</div>
          )}
        </div>
      ),
    },
    { title: 'Info', key: 'meta', width: 200, render: (_, record) => renderMeta(record) },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => <StatusPill status={status} />,
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 130,
      sorter: (x, y) => new Date(toDate(x.createdAt)) - new Date(toDate(y.createdAt)),
      render: (createdAt) => (
        <div className="text-[12px] leading-tight">
          <div className="text-slate-700">{fmtDate(createdAt, 'YYYY-MM-DD')}</div>
          <div className="text-slate-400">{fmtDate(createdAt, 'HH:mm')}</div>
        </div>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 110,
      align: 'right',
      render: (_, record) => (
        <div className="flex items-center justify-end gap-0.5">
          <Tooltip title="상세 보기">
            <button type="button" onClick={() => handleView(record)} className="flex !h-7 !min-h-0 !w-7 !min-w-0 items-center justify-center rounded-md !p-0 text-slate-400 hover:bg-slate-100 hover:text-slate-700">
              <Eye className="h-3.5 w-3.5" />
            </button>
          </Tooltip>
          {canEdit && (
            <Tooltip title="수정">
              <button type="button" onClick={() => handleEdit(record)} className="flex !h-7 !min-h-0 !w-7 !min-w-0 items-center justify-center rounded-md !p-0 text-slate-400 hover:bg-indigo-50 hover:text-indigo-600">
                <Edit className="h-3.5 w-3.5" />
              </button>
            </Tooltip>
          )}
          <Tooltip title="삭제">
            <button type="button" onClick={() => handleDelete(record)} className="flex !h-7 !min-h-0 !w-7 !min-w-0 items-center justify-center rounded-md !p-0 text-slate-400 hover:bg-red-50 hover:text-red-600">
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </Tooltip>
        </div>
      ),
    },
  ];

  const totalPosts =
    (stats.totalStoryPosts || 0) + (stats.totalPhotoPosts || 0) + (stats.totalMemoryPosts || 0) + (stats.totalCommunityPosts || 0);
  const totalGroupsAll = (stats.totalGroups || 0) + (stats.totalStudyGroups || 0);

  const HEADER_STATS = [
    { label: 'Active Users (30d)', value: stats.activeUsers, Icon: UserCheck, cls: 'bg-orange-50 text-orange-600' },
    { label: 'Posts', value: totalPosts, Icon: MessageSquare, cls: 'bg-indigo-50 text-indigo-600' },
    { label: 'Groups', value: totalGroupsAll, Icon: Users, cls: 'bg-blue-50 text-blue-600' },
    { label: 'Messages', value: stats.totalMessages, Icon: MessageCircle, cls: 'bg-teal-50 text-teal-600' },
  ];

  const renderOverview = () => {
    const recentPosts = [...communityPosts]
      .sort((x, y) => new Date(toDate(y.createdAt)) - new Date(toDate(x.createdAt)))
      .slice(0, 6);
    return (
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-2 md:grid-cols-3 xl:grid-cols-5">
          {tabs
            .filter((tb) => tb.key !== 'overview')
            .map((tb) => {
              const Icon = tb.icon;
              return (
                <button
                  key={tb.key}
                  type="button"
                  onClick={() => handleTabChange(tb.key)}
                  className="group flex !min-h-0 items-center gap-3 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:border-slate-300 hover:shadow"
                >
                  <span className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg ${TAB_ACCENT[tb.key]}`}>
                    <Icon className="h-4 w-4" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[11px] text-slate-500">{tb.label}</span>
                    <span className="block text-lg font-bold leading-tight text-slate-900 tabular-nums">
                      {(stats[TAB_STAT_KEY[tb.key]] || 0).toLocaleString()}
                    </span>
                  </span>
                  <ChevronRightIcon />
                </button>
              );
            })}
          <button
            type="button"
            onClick={() => navigate('/marketplace-management')}
            className="group flex !min-h-0 items-center gap-3 rounded-xl border border-dashed border-amber-300 bg-amber-50/40 px-3 py-2.5 text-left transition-all hover:-translate-y-0.5 hover:bg-amber-50"
          >
            <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-amber-100 text-amber-600">
              <ShoppingBag className="h-4 w-4" />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[11px] text-amber-700">Marketplace · 중고장터 관리</span>
              <span className="block text-lg font-bold leading-tight text-slate-900 tabular-nums">
                {(stats.totalMarketplaceItems || 0).toLocaleString()}
              </span>
            </span>
            <ChevronRightIcon />
          </button>
        </div>

        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-3 py-2">
            <h3 className="text-[13px] font-semibold text-slate-800">Recent Community Board posts</h3>
            <button
              type="button"
              onClick={() => handleTabChange('communityPosts')}
              className="!min-h-0 text-[11px] font-semibold text-indigo-600 hover:text-indigo-800"
            >
              View all →
            </button>
          </div>
          {recentPosts.length === 0 ? (
            <div className="py-8">
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={loading ? 'Loading...' : 'No recent posts'} />
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {recentPosts.map((post) => (
                <button
                  key={post.id}
                  type="button"
                  onClick={() => {
                    setActiveTab('communityPosts');
                    setViewItem(post);
                  }}
                  className="flex w-full !min-h-0 items-center gap-3 px-3 py-2 text-left hover:bg-slate-50"
                >
                  <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-sky-50 text-[11px] font-bold text-sky-600">
                    {(post.authorName || '?').charAt(0).toUpperCase()}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[13px] font-medium text-slate-800">{getItemTitle(post)}</span>
                    <span className="block truncate text-[11px] text-slate-500">
                      {post.authorName} · {fmtDate(post.createdAt)}
                    </span>
                  </span>
                  <span className="hidden flex-shrink-0 items-center gap-2 text-[11px] text-slate-400 sm:flex">
                    <span>♥ {post.likeCount || 0}</span>
                    <span>💬 {post.comments || 0}</span>
                  </span>
                  <StatusPill status={post.status} />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  function ChevronRightIcon() {
    return (
      <svg className="h-3.5 w-3.5 flex-shrink-0 text-slate-300 transition-colors group-hover:text-slate-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
      </svg>
    );
  }

  const viewDetails = viewItem
    ? [
        ['Author', viewItem.authorName || viewItem.userName],
        ['Email / ID', viewItem.authorEmail || viewItem.userEmail || viewItem.authorId],
        ['Recipient', activeTab === 'messages' ? viewItem.recipientName : null],
        ['Category', viewItem.category || viewItem.subject],
        ['Level', viewItem.level],
        ['Region', viewItem.region && viewItem.region !== 'Unknown' ? viewItem.region : null],
        ['Members', viewItem.memberCount || null],
        ['Likes', viewItem.likeCount || null],
        ['Comments', typeof viewItem.comments === 'number' ? viewItem.comments : null],
        ['Views', viewItem.viewCount || null],
        ['Created', fmtDate(viewItem.createdAt)],
        ['Updated', fmtDate(viewItem.updatedAt)],
        ['Document ID', viewItem.id],
      ].filter(([, v]) => v !== null && v !== undefined && v !== '')
    : [];

  return (
    <div className="min-h-screen bg-slate-50 p-3">
      <div className="space-y-3">
        {/* Header */}
        <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2.5">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-900">
                <Users className="h-4 w-4 text-white" />
              </div>
              <div>
                <h1 className="text-[15px] font-semibold leading-tight text-slate-900">Community Management</h1>
                <p className="text-[11px] leading-tight text-slate-500">Community content, groups and user interactions</p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {HEADER_STATS.map(({ label, value, Icon, cls }) => (
                <div key={label} className="flex items-center gap-2 rounded-lg border border-slate-200 px-2.5 py-1.5">
                  <span className={`flex h-6 w-6 items-center justify-center rounded-md ${cls}`}>
                    <Icon className="h-3.5 w-3.5" />
                  </span>
                  <span>
                    <span className="block text-[10px] leading-tight text-slate-500">{label}</span>
                    <span className="block text-sm font-bold leading-tight text-slate-900 tabular-nums">{(value || 0).toLocaleString()}</span>
                  </span>
                </div>
              ))}
              <button
                type="button"
                onClick={handleRefresh}
                disabled={loading}
                className="inline-flex !h-8 !min-h-0 items-center gap-1.5 rounded-md border border-slate-200 bg-white !px-3 !py-0 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
              >
                <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
              {canCreate && (
                <button
                  type="button"
                  onClick={handleCreate}
                  className="inline-flex !h-8 !min-h-0 items-center gap-1.5 rounded-md bg-slate-900 !px-3 !py-0 text-xs font-semibold text-white hover:bg-slate-800"
                >
                  <Plus className="h-3.5 w-3.5" />
                  {activeTab === 'communityPosts' ? 'New Post' : 'New Resource'}
                </button>
              )}
            </div>
          </div>

          {/* Tab bar */}
          <div className="-mx-1 mt-3 flex gap-1 overflow-x-auto border-t border-slate-100 px-1 pt-2.5">
            {tabs.map((tb) => {
              const Icon = tb.icon;
              const active = activeTab === tb.key;
              const count = TAB_STAT_KEY[tb.key] ? stats[TAB_STAT_KEY[tb.key]] || 0 : null;
              return (
                <button
                  key={tb.key}
                  type="button"
                  onClick={() => handleTabChange(tb.key)}
                  className={`inline-flex !h-8 !min-h-0 flex-shrink-0 items-center gap-1.5 rounded-lg !px-2.5 !py-0 text-xs font-semibold transition-colors ${
                    active ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  <Icon className="h-3.5 w-3.5" />
                  {tb.label}
                  {count !== null && (
                    <span className={`rounded-full px-1.5 text-[10px] tabular-nums ${active ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-500'}`}>
                      {count.toLocaleString()}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Content */}
        {activeTab === 'overview' ? (
          renderOverview()
        ) : (
          <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            {/* Toolbar */}
            <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2">
              <div className="mr-1 min-w-0">
                <div className="text-[13px] font-semibold text-slate-800">{currentTab.label}</div>
                <div className="truncate text-[11px] text-slate-500">{currentTab.description}</div>
              </div>
              <Input
                allowClear
                size="small"
                prefix={<Search className="h-3.5 w-3.5 text-slate-400" />}
                placeholder="제목, 내용, 작성자 검색..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className="!h-8 !w-64"
              />
              <div className="flex flex-wrap items-center gap-1">
                {[['all', getCurrentData().length], ...statusFilterOptions].map(([st, cnt]) => (
                  <button
                    key={st}
                    type="button"
                    onClick={() => handleStatusChange(st)}
                    className={`inline-flex !h-7 !min-h-0 items-center gap-1 rounded-full !px-2.5 !py-0 text-[11px] font-semibold capitalize ring-1 transition-colors ${
                      filterStatus === st ? 'bg-slate-900 text-white ring-slate-900' : 'bg-white text-slate-600 ring-slate-200 hover:bg-slate-100'
                    }`}
                  >
                    {st}
                    <span className="tabular-nums opacity-70">{cnt}</span>
                  </button>
                ))}
              </div>
              <div className="ml-auto flex items-center gap-2">
                <span className="text-[11px] text-slate-500">
                  {filteredData.length.toLocaleString()}
                  {filteredData.length !== getCurrentData().length ? ` / ${getCurrentData().length.toLocaleString()}` : ''} items
                </span>
                {selectedItems.length > 0 && (
                  <button
                    type="button"
                    onClick={handleBulkDelete}
                    disabled={bulkDeleting}
                    className="inline-flex !h-8 !min-h-0 items-center gap-1.5 rounded-md bg-red-600 !px-3 !py-0 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-60"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    선택 삭제 ({selectedItems.length})
                  </button>
                )}
              </div>
            </div>

            <Table
              columns={getColumns()}
              dataSource={filteredData}
              rowKey={getRowKey}
              loading={loading}
              size="small"
              rowSelection={{
                selectedRowKeys: selectedItems,
                onChange: (keys) => setSelectedItems(keys),
                preserveSelectedRowKeys: true,
              }}
              locale={{
                emptyText: (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={searchTerm || filterStatus !== 'all' ? '조건에 맞는 항목이 없습니다' : '데이터가 없습니다'}
                  />
                ),
              }}
              pagination={{
                current: currentPage,
                pageSize: 20,
                total: filteredData.length,
                onChange: (page) => setCurrentPage(page),
                showSizeChanger: false,
                size: 'small',
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
              }}
              scroll={{ x: 1000 }}
            />
          </div>
        )}
      </div>

      {/* Detail drawer */}
      <Drawer
        open={!!viewItem}
        onClose={() => setViewItem(null)}
        width={520}
        title={
          viewItem && (
            <div className="flex items-center gap-2">
              <span className="truncate text-[15px] font-semibold">{getItemTitle(viewItem)}</span>
              <StatusPill status={viewItem.status} />
            </div>
          )
        }
        footer={
          viewItem && (
            <div className="flex justify-end gap-2">
              {canEdit && (
                <Button
                  icon={<Edit className="h-3.5 w-3.5" />}
                  onClick={() => {
                    const item = viewItem;
                    setViewItem(null);
                    handleEdit(item);
                  }}
                >
                  수정
                </Button>
              )}
              <Button danger icon={<Trash2 className="h-3.5 w-3.5" />} onClick={() => handleDelete(viewItem)}>
                삭제
              </Button>
            </div>
          )
        }
      >
        {viewItem && (
          <div className="space-y-4">
            {getItemImage(viewItem) && (
              <img src={getItemImage(viewItem)} alt="" className="max-h-72 w-full rounded-lg bg-slate-100 object-contain ring-1 ring-slate-200" />
            )}
            {getItemDesc(viewItem) && (
              <div className="whitespace-pre-wrap break-words rounded-lg bg-slate-50 p-3 text-[13px] leading-relaxed text-slate-700 ring-1 ring-slate-200">
                {getItemDesc(viewItem)}
              </div>
            )}
            <dl className="grid grid-cols-3 gap-x-3 gap-y-2 text-[12px]">
              {viewDetails.map(([k, v]) => (
                <React.Fragment key={k}>
                  <dt className="text-slate-500">{k}</dt>
                  <dd className="col-span-2 break-all font-medium text-slate-800">{String(v)}</dd>
                </React.Fragment>
              ))}
            </dl>
          </div>
        )}
      </Drawer>

      {/* Modals */}
      <Modal
        title={activeTab === 'communityPosts' ? '새 게시글 작성' : activeTab === 'learningResources' ? '새 학습 자료' : 'Create New Item'}
        open={isCreateModalOpen}
        onCancel={() => setIsCreateModalOpen(false)}
        footer={null}
        width={600}
      >
        <Form 
          form={createForm} 
          layout="vertical"
          onFinish={activeTab === 'learningResources' ? handleCreateLearningResource : activeTab === 'marketplace' ? handleCreateMarketplaceItem : activeTab === 'communityPosts' ? handleCreateCommunityPost : undefined}
        >
          <Form.Item name="title" label="Title" rules={[{ required: true }]}>
            <Input placeholder="Enter title" />
          </Form.Item>
          
          {activeTab === 'learningResources' ? (
            <>
              <Form.Item name="description" label="Description" rules={[{ required: true }]}>
                <Input.TextArea rows={3} placeholder="Enter learning resource description" />
              </Form.Item>
              <Form.Item name="content" label="Content" rules={[{ required: true }]}>
                <Input.TextArea rows={6} placeholder="Enter learning content" />
              </Form.Item>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="category" label="Category" initialValue="grammar">
                    <Select>
                      <Option value="grammar">Grammar</Option>
                      <Option value="vocabulary">Vocabulary</Option>
                      <Option value="pronunciation">Pronunciation</Option>
                      <Option value="conversation">Conversation</Option>
                      <Option value="reading">Reading</Option>
                      <Option value="writing">Writing</Option>
                      <Option value="listening">Listening</Option>
                      <Option value="culture">Culture</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="level" label="Level" initialValue="beginner">
                    <Select>
                      <Option value="beginner">Beginner</Option>
                      <Option value="intermediate">Intermediate</Option>
                      <Option value="advanced">Advanced</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="language" label="Language" initialValue="ko">
                    <Select>
                      <Option value="ko">Korean</Option>
                      <Option value="my">Myanmar</Option>
                      <Option value="en">English</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="authorName" label="Author Name" initialValue="Admin">
                    <Input placeholder="Enter author name" />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item name="tags" label="Tags">
                <Select mode="tags" placeholder="Enter tags">
                  <Option value="korean">Korean</Option>
                  <Option value="grammar">Grammar</Option>
                  <Option value="beginner">Beginner</Option>
                  <Option value="intermediate">Intermediate</Option>
                  <Option value="advanced">Advanced</Option>
                </Select>
              </Form.Item>
              <Form.Item name="isPublic" label="Public" valuePropName="checked" initialValue={true}>
                <input type="checkbox" />
              </Form.Item>
            </>
          ) : activeTab === 'marketplace' ? (
            <>
              <Form.Item name="description" label="Description" rules={[{ required: true }]}>
                <Input.TextArea rows={3} placeholder="Enter item description" />
              </Form.Item>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="type" label="Type" initialValue="selling">
                    <Select>
                      <Option value="selling">Sell (selling)</Option>
                      <Option value="buying">Buy (buying)</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="price" label="Price (₩)">
                    <Input type="number" placeholder="Enter price" />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="category" label="Category">
                    <Select placeholder="Select category">
                      <Option value="electronics">Electronics</Option>
                      <Option value="furniture">Furniture</Option>
                      <Option value="clothing">Clothing</Option>
                      <Option value="books">Books</Option>
                      <Option value="home">Home & Garden</Option>
                      <Option value="sports">Sports</Option>
                      <Option value="automotive">Automotive</Option>
                      <Option value="other">Other</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="condition" label="Condition" initialValue="good">
                    <Select>
                      <Option value="new">New</Option>
                      <Option value="like_new">Like New</Option>
                      <Option value="good">Good</Option>
                      <Option value="fair">Fair</Option>
                      <Option value="poor">Poor</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="location" label="Location">
                    <Input placeholder="Enter location" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="contactMethod" label="Contact Method" initialValue="message">
                    <Select>
                      <Option value="message">Message</Option>
                      <Option value="phone">Phone</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item name="imageUrl" label="Image URL">
                <Input placeholder="Enter image URL (optional)" />
              </Form.Item>
              <Form.Item name="status" label="Status" initialValue="active">
                <Select>
                  <Option value="active">Active</Option>
                  <Option value="inactive">Inactive</Option>
                  <Option value="sold">Sold</Option>
                </Select>
              </Form.Item>
            </>
          ) : activeTab === 'communityPosts' ? (
            <>
              <Form.Item name="content" label="Content" rules={[{ required: true, message: '내용을 입력하세요' }]}>
                <Input.TextArea rows={6} placeholder="게시글 내용을 입력하세요" />
              </Form.Item>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="category" label="Category" initialValue="General">
                    <Input placeholder="General" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="status" label="Status" initialValue="active">
                    <Select>
                      <Option value="active">Active</Option>
                      <Option value="inactive">Inactive</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
            </>
          ) : (
            <>
              <Form.Item name="description" label="Description">
                <Input.TextArea rows={4} placeholder="Enter description" />
              </Form.Item>
              <Form.Item name="status" label="Status" initialValue="active">
                <Select>
                  {statusOptions.map(option => (
                    <Option key={option.value} value={option.value}>
                      {option.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </>
          )}
          
          <div className="flex justify-end space-x-2">
            <Button onClick={() => setIsCreateModalOpen(false)}>Cancel</Button>
            <Button type="primary" htmlType="submit">
              {activeTab === 'learningResources' ? 'Create Learning Resource' : activeTab === 'marketplace' ? 'Create Marketplace Item' : activeTab === 'communityPosts' ? '게시' : 'Create'}
            </Button>
              </div>
        </Form>
      </Modal>

      <Modal
        title={activeTab === 'learningResources' ? 'Edit Learning Resource' : activeTab === 'communityPosts' ? '게시글 수정' : 'Edit Item'}
        open={isEditModalOpen}
        onCancel={() => setIsEditModalOpen(false)}
        footer={null}
        width={600}
      >
        <Form 
          form={editForm} 
          layout="vertical"
          onFinish={activeTab === 'learningResources' ? handleUpdateLearningResource : activeTab === 'marketplace' ? handleUpdateMarketplaceItem : activeTab === 'communityPosts' ? handleUpdateCommunityPost : undefined}
        >
          <Form.Item name="title" label="Title" rules={[{ required: true }]}>
            <Input placeholder="Enter title" />
          </Form.Item>
          
          {activeTab === 'learningResources' ? (
            <>
              <Form.Item name="description" label="Description" rules={[{ required: true }]}>
                <Input.TextArea rows={3} placeholder="Enter learning resource description" />
              </Form.Item>
              <Form.Item name="content" label="Content" rules={[{ required: true }]}>
                <Input.TextArea rows={6} placeholder="Enter learning content" />
              </Form.Item>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="category" label="Category">
                    <Select>
                      <Option value="grammar">Grammar</Option>
                      <Option value="vocabulary">Vocabulary</Option>
                      <Option value="pronunciation">Pronunciation</Option>
                      <Option value="conversation">Conversation</Option>
                      <Option value="reading">Reading</Option>
                      <Option value="writing">Writing</Option>
                      <Option value="listening">Listening</Option>
                      <Option value="culture">Culture</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="level" label="Level">
                    <Select>
                      <Option value="beginner">Beginner</Option>
                      <Option value="intermediate">Intermediate</Option>
                      <Option value="advanced">Advanced</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="language" label="Language">
                    <Select>
                      <Option value="ko">Korean</Option>
                      <Option value="my">Myanmar</Option>
                      <Option value="en">English</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="authorName" label="Author Name">
                    <Input placeholder="Enter author name" />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item name="tags" label="Tags">
                <Select mode="tags" placeholder="Enter tags">
                  <Option value="korean">Korean</Option>
                  <Option value="grammar">Grammar</Option>
                  <Option value="beginner">Beginner</Option>
                  <Option value="intermediate">Intermediate</Option>
                  <Option value="advanced">Advanced</Option>
                </Select>
              </Form.Item>
              <Form.Item name="isPublic" label="Public" valuePropName="checked">
                <input type="checkbox" />
              </Form.Item>
            </>
          ) : activeTab === 'marketplace' ? (
            <>
              <Form.Item name="description" label="Description" rules={[{ required: true }]}>
                <Input.TextArea rows={3} placeholder="Enter item description" />
              </Form.Item>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="type" label="Type">
                    <Select>
                      <Option value="sell">Sell</Option>
                      <Option value="buy">Buy</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="price" label="Price (₩)">
                    <Input type="number" placeholder="Enter price" />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="category" label="Category">
                    <Select placeholder="Select category">
                      <Option value="electronics">Electronics</Option>
                      <Option value="furniture">Furniture</Option>
                      <Option value="clothing">Clothing</Option>
                      <Option value="books">Books</Option>
                      <Option value="home">Home & Garden</Option>
                      <Option value="sports">Sports</Option>
                      <Option value="automotive">Automotive</Option>
                      <Option value="other">Other</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="condition" label="Condition">
                    <Select>
                      <Option value="new">New</Option>
                      <Option value="like_new">Like New</Option>
                      <Option value="good">Good</Option>
                      <Option value="fair">Fair</Option>
                      <Option value="poor">Poor</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="location" label="Location">
                    <Input placeholder="Enter location" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="contactMethod" label="Contact Method">
                    <Select>
                      <Option value="message">Message</Option>
                      <Option value="phone">Phone</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item name="imageUrl" label="Image URL">
                <Input placeholder="Enter image URL (optional)" />
              </Form.Item>
              <Form.Item name="status" label="Status">
                <Select>
                  <Option value="active">Active</Option>
                  <Option value="inactive">Inactive</Option>
                  <Option value="sold">Sold</Option>
                </Select>
              </Form.Item>
            </>
          ) : activeTab === 'communityPosts' ? (
            <>
              <Form.Item name="content" label="Content" rules={[{ required: true, message: '내용을 입력하세요' }]}>
                <Input.TextArea rows={6} placeholder="게시글 내용을 입력하세요" />
              </Form.Item>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="category" label="Category" initialValue="General">
                    <Input placeholder="General" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="status" label="Status" initialValue="active">
                    <Select>
                      <Option value="active">Active</Option>
                      <Option value="inactive">Inactive</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
            </>
          ) : (
            <>
              <Form.Item name="description" label="Description">
                <Input.TextArea rows={4} placeholder="Enter description" />
              </Form.Item>
              <Form.Item name="status" label="Status">
                <Select>
                  {statusOptions.map(option => (
                    <Option key={option.value} value={option.value}>
                      {option.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </>
          )}
          
          <div className="flex justify-end space-x-2">
            <Button onClick={() => setIsEditModalOpen(false)}>Cancel</Button>
            <Button type="primary" htmlType="submit">
              {activeTab === 'learningResources' ? 'Update Learning Resource' : activeTab === 'marketplace' ? 'Update Marketplace Item' : activeTab === 'communityPosts' ? '저장' : 'Update'}
            </Button>
              </div>
        </Form>
      </Modal>

    </div>
  );
};

export default CommunityManagementPage;